import NextLink from 'next/link';

import {Text} from '#component/base';
import {Sprinkles, sprinkles} from '#style/sprinkles';

import {UnderstandResultsCardNarrow} from '../CTACardNarrow';
import {ReferralCardRow} from '../CTACardWide';
import {CTACard} from './CTACard';

import {CTACardProps} from './CTACard.types';

import {
  referAFriendBg,
  surpriseByYourResultBg,
  TallyTestKitCardBg,
  upgradeAllProductsBg,
} from './CTACard.css';

export * from './CTACard';

export const UnderstandResultsCard = ({
  hasLabAssessment = false,
  columnSpan = 8,
}: {
  columnSpan?: Sprinkles['gridColumnSpan'];
  hasLabAssessment: boolean;
}) =>
  hasLabAssessment ? (
    <CTACard
      columnSpan={columnSpan}
      headline="Surprised by your results?"
      href="/surprisedByYourResults"
      imageUrl="/image/surprised-results.jpg"
      lede="There are many factors that influence how someone ages and we’re here to help you make sense of it."
      objectPosition="0 15%"
      btnColor="primary-dark"
    />
  ) : (
    <CTACard
      columnSpan={[8, 4, 8]}
      headline="Understanding your results"
      href="/understandingYourAge"
      imageUrl="/image/understand-results.jpg"
      lede="Your results are more than just a number.  Break down the science underlying your epigenetic age."
      btnColor="primary-dark"
    />
  );

export const SurpriseByYourResultCard = ({
  columnSpan = 8,
}: {
  columnSpan?: Sprinkles['gridColumnSpan'];
}) => (
  <CTACard
    columnSpan={columnSpan}
    headline="Surprised by your results?"
    href="/surprisedByYourResults"
    imageUrl="/image/surprised-results-latest.png"
    lede="There are many factors that influence how someone ages and we’re here to help you make sense of it."
    objectPosition="0 15%"
    variant="primary"
    btnColor="primary-dark"
    hasOverlay={false}
    className={surpriseByYourResultBg}
    fadeImage={false}
  />
);

const LedeActionPlan = () => (
  <Text as="p" color="mineShaft" variant="body">
    <Text variant="body" color="burgundy" className={sprinkles({textDecoration: 'underline'})}>
      Become a member
    </Text>{' '}
    to access personalized Action Plans.
  </Text>
);

export const UnlockActionPlans = ({columnSpan = 8}: CTACardProps) => (
  <CTACard
    columnSpan={columnSpan}
    fadeImage={false}
    headline="Unlock Action Plan"
    icon="CircleWavyCheck"
    imageUrl="/image/unlock.png"
    lede={<LedeActionPlan />}
    objectPosition="top right"
    variant="secondary"
    showButton={false}
    href="/profile?tab=manage"
  />
);

// Reactivate your membership to regain access
// to personalized Action Plans.
const LedeActionPlanPausedMembers = () => (
  <Text as="p" color="mineShaft" variant="body">
    <Text variant="body" color="burgundy">
      Reactivate your membership{' '}
    </Text>
    to regain access <br /> to personalized Action Plans.
  </Text>
);
export const UnlockActionPlansPausedMember = ({columnSpan = 8}: CTACardProps) => (
  <CTACard
    columnSpan={columnSpan}
    fadeImage={false}
    headline="Unlock Action Plan"
    icon="ClipboardText"
    imageUrl="/image/unlock.png"
    lede={<LedeActionPlanPausedMembers />}
    objectPosition="top right"
    variant="secondary"
    showButton={false}
    href="/profile?tab=manage"
  />
);

const LedeCheckInCardPausedMember = () => (
  <Text as="p" color="mineShaft" variant="body">
    <Text variant="body" color="burgundy">
      Reactivate your membership{' '}
    </Text>
    to regain access to personalized Action Plans.
  </Text>
);
export const CheckInCardToPausedMember = ({columnSpan = [8, 4]}: CTACardProps) => (
  <CTACard
    columnSpan={columnSpan}
    fadeImage={false}
    headline="Unlock Check-ins"
    href="/profile?tab=manage"
    icon="CircleWavyCheck"
    imageUrl="/image/unlock.png"
    lede={<LedeCheckInCardPausedMember />}
    objectPosition="top right"
    showButton={false}
    variant="secondary"
  />
);
const LedeCheckInActive = () => (
  <Text as="p" color="mineShaft" variant="body">
    <NextLink href="/actionplan" passHref>
      <Text as="a" variant="body" color="burgundy">
        Activate your Action Plan{' '}
      </Text>
    </NextLink>
    to enable access to the check-in feature.
  </Text>
);
export const CheckInCardActiveMember = () => (
  <CTACard
    columnSpan={[8, 4]}
    fadeImage={false}
    headline="Check-ins"
    href="/actionplan"
    icon="CircleWavyCheck"
    imageUrl="/image/unlock.png"
    lede={<LedeCheckInActive />}
    objectPosition="top right"
    showButton={false}
    variant="secondary"
  />
);
const LedeCheckInCardCancelledMember = () => (
  <Text as="p" color="mineShaft" variant="body">
    <NextLink href="/profile?tab=manage" passHref>
      <Text as="a" variant="body" color="burgundy">
        Become a member{' '}
      </Text>
    </NextLink>
    to access personalized Action Plans.
  </Text>
);
export const CheckInCardCancelledMember = () => (
  <CTACard
    columnSpan={[8, 4]}
    fadeImage={false}
    headline="Unlock your Action Plan"
    href="/profile?tab=manage"
    icon="CircleWavyCheck"
    imageUrl="/image/unlock.png"
    lede={<LedeCheckInCardCancelledMember />}
    objectPosition="top right"
    showButton={false}
    variant="secondary"
  />
);
export const TallyTestKitCard = (props: CTACardProps) => (
  <CTACard
    columnSpan={[8, 4]}
    hasOverlay={false}
    className={TallyTestKitCardBg}
    showButton={false}
    {...props}
  />
);

export const ReferAFriendCard = ({columnSpan = 8}: {columnSpan?: Sprinkles['gridColumnSpan']}) => (
  <CTACard
    columnSpan={columnSpan}
    headline="Give $30, Get $30"
    href="/shop/pages/referrals"
    imageUrl="/image/referral-wide-card.png"
    lede="Longevity loves company. Refer a friend and you'll both receive a $30 credit towards your next order."
    objectPosition="0 15%"
    variant="primary"
    btnColor="primary-dark"
    hasOverlay={false}
    className={referAFriendBg}
    fadeImage={false}
  />
);

export const UpgradeAllProductsCard = ({
  columnSpan = 8,
}: {
  columnSpan?: Sprinkles['gridColumnSpan'];
}) => (
  <CTACard
    columnSpan={columnSpan}
    headline="The future of healthy aging"
    href="/shop/products/membership"
    imageUrl="/image/profile/upsell-upgrade-all-products.png"
    lede="Take control over how you age. Unlock healthier years with science-backed tools designed to improve your healthspan and longevity."
    objectPosition="0 15%"
    variant="textBottom"
    btnColor="primary"
    hasOverlay={false}
    className={upgradeAllProductsBg}
    fadeImage={false}
  />
);

export const CTACardWrapperMobile = ({children}: {children: React.ReactNode}) => (
  <div className={sprinkles({display: ['grid', 'grid', 'none'], colSpan: 8})}>{children}</div>
);
export const CTACardWrapperDesktop = ({children}: {children: React.ReactNode}) => (
  <div className={sprinkles({display: ['none', 'none', 'grid'], colSpan: 8})}>{children}</div>
);
export const CTACardWrapperDesktopEntireRow = ({children}: {children: React.ReactNode}) => (
  <div
    className={sprinkles({display: ['none', 'none', 'grid'], colSpan: 8})}
    style={{gridColumn: '1 / -1'}}>
    {children}
  </div>
);

export const ReferralCardEntireRow = () => (
  <>
    <CTACardWrapperDesktopEntireRow>
      <ReferralCardRow />
    </CTACardWrapperDesktopEntireRow>
    <CTACardWrapperMobile>
      <ReferAFriendCard />
    </CTACardWrapperMobile>
  </>
);

export const UnderstandResultsCompleted = ({
  hasLabAssessment = false,
}: {
  hasLabAssessment: boolean;
}) => (
  <>
    <CTACardWrapperDesktop>
      <UnderstandResultsCard hasLabAssessment={hasLabAssessment} />
    </CTACardWrapperDesktop>
    <CTACardWrapperMobile>
      <UnderstandResultsCardNarrow hasLabAssessment={hasLabAssessment} />
    </CTACardWrapperMobile>
  </>
);
